/// <amd-module name="VSS/Features/ReduxDirectory/Redux/DirectorySelectors" />
import * as DirectoryResources from "VSS/Features/Directory/Resources";

import { FavoriteStatus, IDirectoryAwareState, IDirectoryRowGroup, IDirectoryViewState, IFavoriteState } from "Contracts";
import  * as Reselect from "Reselect";
import { caseInsensitiveContains, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { DirectoryFilterKeys, DirectoryGroupKeys } from "VSS/Features/Directory/DirectoryConstants";
import { ExceptionInfo, IArtifactOwner, IDirectoryArtifact, IFavoriteData } from "VSS/Features/Directory/DirectoryContracts";
import { IDirectoryRow, IGridRow, IGroupRow, ILoadingRow, IZeroDataRow } from "VSS/Features/Directory/DirectoryGrid";
import { getCurrentFilterState, isFilterActive } from "VSS/Redux/Deprecated/HubViewState";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { IVssIconProps } from "VSSUI/VssIcon";

/* ---------------
  BASIC SELECTORS
----------------*/

/** Get the current artifacts */
export function getArtifactMap<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: T } {
    return state.directoryState.directoryArtifactState.artifacts || {};
}

/** Get the current customGroupMappings */
export function getCustomGroupingsMap<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: string[] } {
    return state.directoryState.directoryArtifactState.customKeyGroupings || {};
}

/** Get the deleted artifacts */
export function getDeletedArtifactMap<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: T } {
    return state.directoryState.directoryArtifactState.deletedArtifacts || {};
}

/** Creates a selector that retrieves an artifact by id */
export function getArtifactById<T extends IDirectoryArtifact>(artifactId: string): (state: IDirectoryViewState<T>) => T | undefined {
    return (state: IDirectoryViewState<T>): T => {
        return state.directoryState.directoryArtifactState.artifacts[artifactId];
    };
}

/** Get the custom grouping artifact keys */
export function getCustomGroupingKeys<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): string[] {
    return state.directoryState.directoryArtifactState.customKeyGroupings
        ? Object.keys(state.directoryState.directoryArtifactState.customKeyGroupings)
        : [];
}

/** Get the recent artifact keys */
export function getRecentKeys<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): string[] {
    return state.directoryState.directoryArtifactState.recentArtifactKeys || [];
}

/** Get the favorites state */
export function getFavoriteState<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): IFavoriteState {
    return state.directoryState.favoriteState;
}

/** Get the status of a specific favorite */
export function getFavoriteStatus<T extends IDirectoryArtifact>(artifactId: string): (state: IDirectoryViewState<T>) => FavoriteStatus {
    return (state: IDirectoryViewState<T>) => getFavoriteState(state).favoriteStatuses[artifactId];
}

/** Get the status of a specific favorite */
export function getFavoriteStatuses<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: FavoriteStatus } {
    return getFavoriteState(state).favoriteStatuses;
}

/** Get the permission for viewing favorites UI. */
export function getFavoritesViewPermission<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): boolean {
    return state.directoryState.favoriteState.favoritesViewPermission;
}

export function getTtiReadyState<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): boolean {
    return state.directoryState.directoryArtifactState.readyToMarkTti;
}

/** Get the grouping collapsed mapping */
export function getGroupCollapsedMap<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: boolean } {
    return state.directoryState.uiState.rowGroupCollapsedMap;
}

export function isFilterVisible<T extends IDirectoryArtifact>(state: IDirectoryAwareState<T>): boolean {
    return state.directoryState.uiState.isFilterBarVisible;
}

/**
 * Gets the exception info, if present
 */
export function getExceptionInfo<T extends IDirectoryArtifact>(state: IDirectoryAwareState<T>): ExceptionInfo | undefined {
    return state.directoryState.uiState.exceptionInfo;
}

/** Is the all section loading? */
export function isAllSectionLoading<T extends IDirectoryArtifact>(state: IDirectoryAwareState<T>): boolean {
    return state.directoryState.uiState.isAllSectionLoading;
}

/** Has the all section ever been populated? */
export function isAllSectionPopulated<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): boolean {
    return state.directoryState.uiState.isAllSectionPopulated;
}

/** Is the page navigating away? */
export function isNavigating<T extends IDirectoryArtifact>(state: IDirectoryAwareState<T>): boolean {
    return state.directoryState.uiState.isNavigating;
}

/** Gets the current favorites */
export function getFavoriteMap<T extends IDirectoryArtifact>(state: IDirectoryAwareState<T>): { [key: string]: IFavoriteData } {
    return state.directoryState.favoriteState.favorites;
}

/** Get any errors associated with the artifacts */
export function getFavoriteErrors<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: Error } {
    return state.directoryState.favoriteState.favoriteErrors;
}

export function getArtifactIcon<T extends IDirectoryArtifact>(
    state: IDirectoryViewState<T>,
    props: { artifactIcon?: IVssIconProps }
): IVssIconProps | undefined {
    return props.artifactIcon;
}

export function getArtifactName<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>, props: { artifactName?: string }): string {
    return props.artifactName || DirectoryResources.DefaultArtifactName;
}

export function getAdditionalKeywordFiterableMembers<T extends IDirectoryArtifact>(
    state: IDirectoryViewState<T>,
    props: { additionalKeywordFilterableMembers: string[] }
): string[] {
    return props.additionalKeywordFilterableMembers || [];
}

export function getAdditionalSortedMembers<T extends IDirectoryArtifact>(
    state: IDirectoryViewState<T>,
    props: { additionalSortedMembers?: string[] }
): string[] | undefined {
    return props.additionalSortedMembers;
}

/**
 * Create selectors that should be disposed when a connected component is unmounted
 */
export function createInstanceSelectors() {
    /** Get a list of favorites */
    const getFavoriteList = Reselect.createSelector(
        [getFavoriteState],
        (favoriteState: IFavoriteState) => {
            const favorites = favoriteState.favorites || {};
            const favoriteStatuses = favoriteState.favoriteStatuses || {};
            return Object.keys(favorites)
                .filter(key => favoriteStatuses[key] === FavoriteStatus.Favoriting || favoriteStatuses[key] === FavoriteStatus.Favorited)
                .map(key => favorites[key]);
        }
    );

    /** Type alias for a function that produces a directory row */
    type DirectoryRowBuilder<T extends IDirectoryArtifact> = (artifact: T, groupKey?: string) => IDirectoryRow<T>;

    /*-------------------
      Artifact selectors
    -------------------*/

    /**
     * Get a sorted list of the artifacts that have been favorited
     * Depends on the {@link getFavoriteList} and {@link getArtifactMap} functions
     */
    const getSortedFavoriteArtifacts = Reselect.createSelector(
        [getFavoriteList, getArtifactMap, getDeletedArtifactMap, getAdditionalSortedMembers],
        <T extends IDirectoryArtifact>(
            favorites: IFavoriteData[],
            artifacts: { [key: string]: T },
            deletedArtifacts: { [key: string]: T },
            getAdditionalSortedMembers: string[]
        ) => {
            return favorites
                .map(favorite => (favorite.isDeleted ? deletedArtifacts[favorite.artifactId] : artifacts[favorite.artifactId]))
                .sort(getCustomFieldComparator(getAdditionalSortedMembers));
        }
    );

    /**
     * Get a sorted list of the recent artifacts
     * Depends on the {@link getRecentKeys} and the {@link getArtifactMap} functions
     */
    const getSortedRecentArtifacts = Reselect.createSelector(
        [getRecentKeys, getArtifactMap, getAdditionalSortedMembers],
        <T extends IDirectoryArtifact>(recentKeys: string[], artifacts: { [key: string]: T }, getAdditionalSortedMembers: string[]): T[] => {
            return recentKeys.map(key => artifacts[key]).sort(getCustomFieldComparator(getAdditionalSortedMembers));
        }
    );

    /**
     * Get a sorted list of the member artifacts
     * Depends on the {@link getCustomGroupingsMap} function
     */
    const getSortedCustomGroupings = Reselect.createSelector(
        [getCustomGroupingsMap, getArtifactMap, getAdditionalSortedMembers],
        <T extends IDirectoryArtifact>(
            groupings: { [key: string]: string[] },
            artifacts: { [key: string]: T },
            getAdditionalSortedMembers: string[]
        ): { [key: string]: T[] } => {
            const keys = Object.keys(groupings).sort();
            const sortedGroups: { [key: string]: T[] } = {};
            keys.forEach(key => {
                sortedGroups[key] = groupings[key].map(id => artifacts[id]).sort(getCustomFieldComparator(getAdditionalSortedMembers));
            });
            return sortedGroups;
        }
    );

    /**
     * Get a sorted list of all the artifacts
     * Depends on the {@link getArtifactMap} function
     */
    const getSortedArtifacts = Reselect.createSelector(
        [getArtifactMap, getAdditionalSortedMembers],
        <T extends IDirectoryArtifact>(artifactMap: { [key: string]: T }, getAdditionalSortedMembers: string[]): T[] => {
            return Object.keys(artifactMap)
                .map(k => artifactMap[k])
                .sort(getCustomFieldComparator(getAdditionalSortedMembers));
        }
    );

    /**
     * Cache of rows that have already been constructed
     */
    const rowCache: { [key: string]: IDirectoryRow<any> } = {};

    /**
     * Selector which returns a "row builder" function. This will composite information from the state with an artifact to build a DirectoryRow
     * for use within the Directory Grid
     */
    const directoryRowBuilder = Reselect.createSelector(
        [getFavoriteMap, getFavoriteStatuses, getFavoriteErrors, getArtifactIcon],
        <T extends IDirectoryArtifact>(
            favorites: { [key: string]: IFavoriteData },
            favoriteStatuses: { [key: string]: FavoriteStatus },
            favoriteErrors: { [key: string]: Error },
            artifactIcon?: IVssIconProps
        ): DirectoryRowBuilder<T> => {
            return (artifact: T, groupKey?: string) => {
                const key = groupKey ? `${groupKey} - ${artifact.id}` : artifact.id;
                const favoriteArtifact: IFavoriteData = favorites[artifact.id];
                const favoriteStatus: FavoriteStatus = favoriteStatuses[artifact.id];
                const isDeleted = favoriteArtifact ? favoriteArtifact.isDeleted : false;
                const isFavorited =
                    !!favoriteArtifact && favoriteStatus !== FavoriteStatus.Unfavorited && favoriteStatus !== FavoriteStatus.Unfavoriting;
                const errorMessage = favoriteErrors[artifact.id] ? favoriteErrors[artifact.id].message : undefined;

                const builtRow = rowCache[key];
                if (
                    !builtRow ||
                    builtRow.isDeleted !== isDeleted ||
                    builtRow.isFavorited !== isFavorited ||
                    builtRow.data !== artifact ||
                    builtRow.errorMessage !== errorMessage ||
                    builtRow.iconProps !== artifactIcon
                ) {
                    rowCache[key] = {
                        key,
                        isDeleted,
                        isFavorited,
                        groupKey: groupKey,
                        data: artifact,
                        errorMessage,
                        iconProps: artifactIcon
                    };
                }

                return rowCache[key];
            };
        }
    );

    /**
     * Given a selector function, construct a group selector which will build a grouping
     * @param groupKey The group key
     * @param groupHeader The header of the group to create
     * @param selector The selector function to use to build the group
     */
    function getGroupSelector<T extends IDirectoryArtifact>(
        groupKey: string,
        groupHeader: string,
        selector: (state: IDirectoryViewState<T>, props: { additionalSortedMembers?: string[] }) => T[],
        includeSuffix: boolean = true,
        toggleable: boolean = true,
        alwaysVisibleSelector: (state: IDirectoryViewState<T>) => boolean = () => false
    ) {
        return Reselect.createSelector(
            // @ts-ignore Broken by TS 3.4 update
            [selector, getGroupCollapsedMap, getArtifactName, alwaysVisibleSelector],
            (items: T[], groupCollapsedMap: { [key: string]: boolean }, artifactName: string, alwaysVisible: boolean): IDirectoryRowGroup<T> => {
                return {
                    alwaysVisible,
                    groupKey,
                    groupHeader: includeSuffix ? `${groupHeader} ${artifactName.toLocaleLowerCase()}` : groupHeader,
                    collapsed: !!groupCollapsedMap[groupKey],
                    items,
                    toggleable
                };
            }
        );
    }

    /**
     * Get the directory custom row groups from the sorted disctionary of custom groupings
     */
    const getCustomGroupRows = Reselect.createSelector(
        [getGroupCollapsedMap, getSortedCustomGroupings],
        <T extends IDirectoryArtifact>(
            groupCollapsedMap: { [key: string]: boolean },
            sortedCustomGroupings: { [key: string]: T[] }
        ): IDirectoryRowGroup<T>[] => {
            return Object.keys(sortedCustomGroupings).map(groupKey => {
                return {
                    groupKey,
                    groupHeader: groupKey,
                    collapsed: !!groupCollapsedMap[groupKey],
                    items: sortedCustomGroupings[groupKey],
                    toggleable: true
                } as IDirectoryRowGroup<T>;
            });
        }
    );

    /** Get the recent grouping */
    const getRecentGroup = getGroupSelector(
        DirectoryGroupKeys.RecentGroupKey,
        DirectoryResources.RecentGroupHeading,
        getSortedRecentArtifacts,
        false,
        true
    );
    /** Get the favorite grouping */
    const getFavoriteGroup = getGroupSelector(
        DirectoryGroupKeys.FavoriteGroupKey,
        DirectoryResources.FavoriteGroupHeading,
        getSortedFavoriteArtifacts,
        true,
        true,
        getFavoritesViewPermission
    );
    /** Get the all grouping */
    const getAllGroup = getGroupSelector(
        DirectoryGroupKeys.AllGroupKey,
        DirectoryResources.AllGroupHeading,
        getSortedArtifacts,
        true,
        true,
        () => true
    );

    /**
     * Selector which takes the Directory groupings (recent, favorite, custom, all) and returns a flat list of rows
     * for use in the Directory Grid component
     *
     * Depends on the {@link getRecentGroup}, {@link getFavoriteGroup}, {@link getMemberGroup}, {@link getAllGroup}, {@link isAllSectionLoading}, and {@link directoryRowBuilder} selectors
     */
    const getGroupedRows = Reselect.createSelector(
        [getRecentGroup, getFavoriteGroup, getCustomGroupRows, getAllGroup, isAllSectionLoading, directoryRowBuilder],
        <T extends IDirectoryArtifact>(
            recentGroup: IDirectoryRowGroup<T>,
            favoriteGroup: IDirectoryRowGroup<T>,
            customGroups: IDirectoryRowGroup<T>[],
            allGroup: IDirectoryRowGroup<T>,
            isAllSectionLoading: boolean,
            buildDirectoryRow: DirectoryRowBuilder<T>
        ): IGridRow<T>[] => {
            const groups = [recentGroup, favoriteGroup, ...customGroups, allGroup];
            const flattenedRows: IGridRow<T>[] = [];

            groups.forEach((group: IDirectoryRowGroup<T>) => {
                // If the group has items, or always show the all group
                if (group.items.length > 0 || (group.alwaysVisible || false)) {
                    const groupRow: IGroupRow = {
                        toggleable: group.toggleable,
                        key: group.groupKey,
                        title: _getGroupTitle(group),
                        collapsed: group.collapsed || false,
                        isGroupRow: true
                    };

                    flattenedRows.push(groupRow);

                    if (!group.collapsed) {
                        // SPECIAL CASE: All Group
                        if (group.groupKey === DirectoryGroupKeys.AllGroupKey && isAllSectionLoading) {
                            const loadingRow: ILoadingRow = {
                                key: `${group.groupKey} - LOADING`,
                                groupKey: group.groupKey,
                                isLoadingRow: true
                            };

                            flattenedRows.push(loadingRow);
                        } else if (group.items.length === 0) {
                            const emptyRow: IZeroDataRow = {
                                key: `${group.groupKey} - EMPTY`,
                                groupKey: group.groupKey,
                                isZeroDataRow: true
                            };

                            flattenedRows.push(emptyRow);
                        } else {
                            group.items.forEach(item => flattenedRows.push(buildDirectoryRow(item, group.groupKey)));
                        }
                    }
                }
            });

            return flattenedRows;
        }
    );

    /**
     * Selector which gets a flat list of filtered rows
     *
     * Depends on the {@link getFilter}, {@link getSortedArtifacts}, and {@link directoryRowBuilder} selectors
     */
    const getFilteredRows = Reselect.createSelector(
        [getCurrentFilterState, getSortedArtifacts, directoryRowBuilder, isAllSectionPopulated, getAdditionalKeywordFiterableMembers],
        <T extends IDirectoryArtifact>(
            filter: IFilterState,
            sortedArtifacts: T[],
            buildDirectoryRow: DirectoryRowBuilder<T>,
            isAllSectionPopulated: boolean,
            additionalKeywordFilterableMembers: string[]
        ): IDirectoryRow<T>[] => {
            return isAllSectionPopulated
                ? sortedArtifacts.filter(a => _artifactMatchesFilter(filter, a, additionalKeywordFilterableMembers)).map(v => buildDirectoryRow(v))
                : [];
        }
    );

    /**
     * Function which either gets the grouped rows, or a flat list of filetered rows, depending on the filter state
     */
    return {
        getDirectoryRows: <T extends IDirectoryArtifact>(
            state: IDirectoryViewState<T>,
            props: { artifactIcon?: IVssIconProps; artifactName?: string; additionalFilterColumns?: string[] }
        ): IGridRow<T>[] => {
            if (!getExceptionInfo(state)) {
                if (isFilterActive(state)) {
                    return getFilteredRows(state, props) as IGridRow<T>[];
                } else {
                    return getGroupedRows(state, props) as IGridRow<T>[];
                }
            }
            return [];
        }
    };
}

export const getAllOwners = Reselect.createSelector(
    [getArtifactMap],
    <T extends IDirectoryArtifact>(artifactMap: { [key: string]: T }) => {
        const ownersMap: { [key: string]: IArtifactOwner } = {};
        for (const artifactId in artifactMap) {
            const artifact = artifactMap[artifactId];
            for (const artifactOwner of artifact.owners) {
                ownersMap[artifactOwner.id] = artifactOwner;
            }
        }

        const ownersList: IArtifactOwner[] = [];
        for (const ownerId in ownersMap) {
            ownersList.push(ownersMap[ownerId]);
        }

        ownersList.sort(_artifactComparator);
        return ownersList;
    }
);

/** Inject custom sorting operations into artifactComparator */
function getCustomFieldComparator<T extends IDirectoryArtifact | IArtifactOwner>(
    additionalSortedMembers: string[]
): (artifactA: T, artifactB: T) => number {
    return (artifactA: T, artifactB: T) => {
        return _artifactComparator(artifactA, artifactB, additionalSortedMembers);
    };
}

/**
 * Compare two artifacts by name
 */
function _artifactComparator<T extends IDirectoryArtifact | IArtifactOwner>(artifactA: T, artifactB: T, additionalSortedMembers?: string[]): number {
    const artifactAExists = artifactA && artifactA.name;
    const artifactBExists = artifactB && artifactB.name;
    if (!artifactAExists && !artifactBExists) {
        return 0;
    }

    if (!artifactAExists) {
        return 1;
    }

    if (!artifactBExists) {
        return -1;
    }

    let comparison = localeIgnoreCaseComparer(artifactA.name.trim(), artifactB.name.trim());
    if (!comparison && additionalSortedMembers) {
        for (const field of additionalSortedMembers) {
            if (comparison !== 0) {
                break;
            }
            const hasPropA = artifactA.hasOwnProperty(field) && typeof (artifactA as any)[field] === "string";
            const hasPropB = artifactB.hasOwnProperty(field) && typeof (artifactB as any)[field] === "string";
            if (hasPropA && hasPropB) {
                comparison = localeIgnoreCaseComparer((artifactA as any)[field].trim(), (artifactB as any)[field].trim());
            } else {
                if (hasPropA) {
                    comparison = -1;
                }
                if (hasPropB) {
                    comparison = 1;
                }
            }
        }
    }
    return comparison;
}

/**
 * Checks if a team matches a filter, by text or team id
 * @param activeFilter The active filter
 * @param team The team
 */
function _artifactMatchesFilter(activeFilter: IFilterState, artifact: IDirectoryArtifact, additionalKeywordFilteredMembers: string[]): boolean {
    let matchesFilter = true;
    const keywordFilterItemState = activeFilter[DirectoryFilterKeys.KEYWORD_FILTER_KEY];
    const keywordFilterValue: string = keywordFilterItemState ? keywordFilterItemState.value : undefined;
    const ownerFilterItemState = activeFilter[DirectoryFilterKeys.TEAM_FILTER_KEY];
    const ownerFilterValue: string[] = ownerFilterItemState ? ownerFilterItemState.value.map((v: any) => v.key) : undefined;

    if (matchesFilter && ownerFilterValue && ownerFilterValue.length) {
        let ownerMatchFound = false;
        if (artifact && artifact.owners) {
            for (const owner of artifact.owners) {
                const ownerId = owner.id;
                if (ownerFilterValue.find(oId => oId === ownerId)) {
                    ownerMatchFound = true;
                    break;
                }
            }
        }

        matchesFilter = ownerMatchFound;
    }

    if (artifact && matchesFilter && keywordFilterValue) {
        matchesFilter = caseInsensitiveContains(artifact.name, keywordFilterValue);

        if (!matchesFilter && additionalKeywordFilteredMembers.length) {
            additionalKeywordFilteredMembers.forEach(field => {
                if (!matchesFilter && artifact.hasOwnProperty(field) && (artifact as any)[field]) {
                    matchesFilter = caseInsensitiveContains((artifact as any)[field], keywordFilterValue);
                }
            });
        }
    }

    return matchesFilter;
}

/**
 * Returns the title string to be used for the given group.
 * @param group the group to use
 */
function _getGroupTitle<T extends IDirectoryArtifact>(group: IDirectoryRowGroup<T>): string {
    const isUncountedGroup = group.groupKey === DirectoryGroupKeys.RecentGroupKey || group.groupKey === DirectoryGroupKeys.AllGroupKey;
    if (isUncountedGroup || !group.items || group.items.length <= 0) {
        return group.groupHeader;
    }

    return `${group.groupHeader} (${group.items.length})`;
}
